import * as turf from '@turf/turf';
import type {
  DetailedCircuitFeature,
  DetailedCircuitGeometry,
  TrackContributionGpsCoord,
  TrackContributionPayload,
} from '@kangaroo/shared';

import { withDerivedGeometricApexes } from './detailedCircuitGeometry';
import {
  buildContributionDraftPayload,
  buildStartLineAtFraction,
  detectCornersFromPolyline,
  detectSegmentsFromPolyline,
  healCircuitPolylineForDisplay,
  type CornerApexSuggestion,
  type GpsCoord,
  type RecalibratedTrackGeometry,
  type SegmentSuggestion,
  type StartLineSuggestion,
} from './trackEnrichment';

const EARTH_RADIUS_M = 6_371_000;
const CATALOG_DIVIDER_OVERSHOOT_M = 8;

export function catalogDividerWidthM(trackWidthM: number) {
  return trackWidthM + CATALOG_DIVIDER_OVERSHOOT_M;
}

export type BuildDetailedGeometryOptions = {
  trackWidthM?: number;
  cornerArcHalfM?: number;
  corners?: CornerApexSuggestion[];
  segments?: SegmentSuggestion[];
  startLine?: StartLineSuggestion;
  lapSections?: CatalogLapSection[];
  authoredSurface?: AuthoredTrackSurface | null;
  customFeatures?: DetailedCircuitFeature[];
};

export type CatalogLapSection = {
  id: string;
  type: 'straight' | 'corner';
  startFraction: number;
  endFraction: number;
  cornerId?: string;
  entryLine?: GpsCoord[];
  exitLine?: GpsCoord[];
};

export type CrossTrackBoundaryLine = {
  id: string;
  boundaryIndex: number;
  pointA: GpsCoord;
  pointB: GpsCoord;
  color: string;
};

export type AuthoredTrackSurface = {
  outsideLine: GpsCoord[];
  insideLine: GpsCoord[];
  matchedOutsideLine: GpsCoord[];
  matchedInsideLine: GpsCoord[];
  centerLine: GpsCoord[];
  outlineRings: TrackContributionGpsCoord[][];
};

export type ReverseCatalogLapSectionsOptions = {
  cornerIdMap?: ReadonlyMap<string, string>;
};

export function reverseCatalogLapSections(
  sections: CatalogLapSection[],
  options: ReverseCatalogLapSectionsOptions = {},
): CatalogLapSection[] {
  return sections
    .map((section) => {
      const remappedCorner = section.cornerId
        ? { cornerId: options.cornerIdMap?.get(section.cornerId) ?? section.cornerId }
        : {};
      return {
        ...section,
        ...remappedCorner,
        startFraction: normalizeFraction(1 - section.endFraction),
        endFraction: normalizeFraction(1 - section.startFraction),
        entryLine: section.exitLine ? [...section.exitLine] : undefined,
        exitLine: section.entryLine ? [...section.entryLine] : undefined,
      };
    })
    .sort((left, right) => left.startFraction - right.startFraction);
}

type LapSection = {
  type: 'corner' | 'straight';
  startFraction: number;
  endFraction: number;
  corner?: CornerApexSuggestion;
  entryLine?: GpsCoord[];
  exitLine?: GpsCoord[];
};

function haversineM(a: GpsCoord, b: GpsCoord) {
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const dLat = lat2 - lat1;
  const dLon = ((b.lon - a.lon) * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(h));
}

function openPolyline(polyline: GpsCoord[]) {
  if (polyline.length >= 2 && haversineM(polyline[0]!, polyline.at(-1)!) <= 1) {
    return polyline.slice(0, -1);
  }
  return polyline;
}

function polylineLengthM(polyline: GpsCoord[]) {
  const open = openPolyline(polyline);
  if (open.length < 2) {
    return 0;
  }

  let total = 0;
  for (let index = 1; index < open.length; index += 1) {
    total += haversineM(open[index - 1]!, open[index]!);
  }
  return total;
}

function normalizeFraction(fraction: number) {
  const wrapped = fraction % 1;
  return wrapped < 0 ? wrapped + 1 : wrapped;
}

function toContributionCoord(coord: GpsCoord): TrackContributionGpsCoord {
  return { lat: coord.lat, lon: coord.lon };
}

function toLineString(coords: GpsCoord[]) {
  return turf.lineString(coords.map((coord) => [coord.lon, coord.lat]));
}

function ringFromTurfPositions(positions: number[][]): TrackContributionGpsCoord[] {
  const ring = positions.flatMap(([lon, lat]) =>
    typeof lon === 'number' && typeof lat === 'number' ? [{ lat, lon }] : [],
  );
  if (ring.length > 1 && haversineM(ring[0]!, ring.at(-1)!) <= 0.5) {
    return ring.slice(0, -1);
  }
  return ring;
}

function bearingDeg(from: GpsCoord, to: GpsCoord) {
  const lat1 = (from.lat * Math.PI) / 180;
  const lat2 = (to.lat * Math.PI) / 180;
  const dLon = ((to.lon - from.lon) * Math.PI) / 180;
  const y = Math.sin(dLon) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

function offsetPointPerpendicular(
  center: GpsCoord,
  trackBearingDeg: number,
  halfWidthM: number,
  side: 'left' | 'right',
): GpsCoord {
  const perp = side === 'left' ? trackBearingDeg - 90 : trackBearingDeg + 90;
  const rad = (perp * Math.PI) / 180;
  const dLat = ((halfWidthM * Math.cos(rad)) / EARTH_RADIUS_M) * (180 / Math.PI);
  const dLon =
    ((halfWidthM * Math.sin(rad)) / (EARTH_RADIUS_M * Math.cos((center.lat * Math.PI) / 180))) * (180 / Math.PI);
  return { lat: center.lat + dLat, lon: center.lon + dLon };
}

function offsetRail(arc: GpsCoord[], halfWidthM: number, side: 'left' | 'right'): GpsCoord[] {
  return arc.map((coord, index) => {
    const previous = arc[Math.max(0, index - 1)]!;
    const next = arc[Math.min(arc.length - 1, index + 1)]!;
    const trackBearing = bearingDeg(previous, next);
    return offsetPointPerpendicular(coord, trackBearing, halfWidthM, side);
  });
}

/** Section/sector fill with flat caps at entry and exit (no rounded buffer ends). */
function flatCapSectionRings(coords: GpsCoord[], halfWidthM: number): TrackContributionGpsCoord[][] {
  if (coords.length < 2 || halfWidthM <= 0) {
    return [];
  }

  const leftRail = offsetRail(coords, halfWidthM, 'left');
  const rightRail = offsetRail(coords, halfWidthM, 'right');
  const ring = [
    ...leftRail.map(toContributionCoord),
    ...rightRail.slice().reverse().map(toContributionCoord),
  ];

  if (ring.length < 4) {
    return [];
  }

  return [ring];
}

function bufferPolylineToRings(coords: GpsCoord[], halfWidthM: number): TrackContributionGpsCoord[][] {
  if (coords.length < 2 || halfWidthM <= 0) {
    return [];
  }

  const buffered = turf.buffer(toLineString(coords), halfWidthM, {
    units: 'meters',
    steps: 10,
  });
  if (!buffered) {
    return [];
  }

  if (buffered.geometry.type === 'Polygon') {
    return buffered.geometry.coordinates.map((ring) => ringFromTurfPositions(ring));
  }

  if (buffered.geometry.type === 'MultiPolygon') {
    return buffered.geometry.coordinates.flatMap((polygon) =>
      polygon.map((ring) => ringFromTurfPositions(ring)),
    );
  }

  return [];
}

function buildCumulativeDistances(polyline: GpsCoord[]) {
  const cumulative = [0];
  for (let index = 1; index < polyline.length; index += 1) {
    cumulative.push(cumulative[index - 1]! + haversineM(polyline[index - 1]!, polyline[index]!));
  }
  return cumulative;
}

function coordAtDistance(polyline: GpsCoord[], cumulative: number[], distance: number): GpsCoord {
  const total = cumulative.at(-1) ?? 0;
  if (polyline.length === 0) {
    return { lat: 0, lon: 0 };
  }
  if (total <= 0) {
    return polyline[0]!;
  }

  const target = ((distance % total) + total) % total;
  let cursor = 0;
  while (cursor < cumulative.length - 2 && cumulative[cursor + 1]! < target) {
    cursor += 1;
  }

  const start = cumulative[cursor]!;
  const end = cumulative[cursor + 1]!;
  const t = end > start ? (target - start) / (end - start) : 0;
  const a = polyline[cursor]!;
  const b = polyline[cursor + 1]!;
  return {
    lat: a.lat + (b.lat - a.lat) * t,
    lon: a.lon + (b.lon - a.lon) * t,
  };
}

export function coordAtPolylineFraction(sourcePolyline: GpsCoord[], fraction: number): GpsCoord | null {
  const polyline = healCircuitPolylineForDisplay(sourcePolyline);
  if (polyline.length < 2) {
    return polyline[0] ?? null;
  }
  const cumulative = buildCumulativeDistances(polyline);
  const total = cumulative.at(-1) ?? 0;
  return total > 0 ? coordAtDistance(polyline, cumulative, normalizeFraction(fraction) * total) : polyline[0]!;
}

export function extractShortestClosedPolylineArc(
  polyline: GpsCoord[],
  startFraction: number,
  endFraction: number,
): GpsCoord[] {
  const open = openPolyline(polyline);
  if (open.length < 2) {
    return open;
  }

  const forwardSpan = normalizeFraction(endFraction - startFraction);
  const direction = forwardSpan <= 0.5 ? 1 : -1;
  const span = direction === 1 ? forwardSpan : 1 - forwardSpan;
  const sampleCount = Math.max(2, Math.ceil(open.length * span));
  return Array.from({ length: sampleCount + 1 }, (_, index) =>
    coordAtPolylineFraction(
      open,
      startFraction + direction * span * (index / sampleCount),
    )!,
  );
}

function resampleClosedPolyline(polyline: GpsCoord[], sampleCount: number): GpsCoord[] {
  const open = openPolyline(polyline);
  if (open.length < 2 || sampleCount < 2) {
    return open;
  }

  const closed = [...open, open[0]!];
  const cumulative = buildCumulativeDistances(closed);
  const total = cumulative.at(-1) ?? 0;
  if (total <= 0) {
    return open;
  }

  return Array.from({ length: sampleCount }, (_, index) =>
    coordAtDistance(closed, cumulative, (index / sampleCount) * total),
  );
}

function smoothClosedRail(polyline: GpsCoord[], iterations = 3, cornerCut = 0.25) {
  let points = openPolyline(polyline);
  if (points.length < 4) {
    return points;
  }

  for (let iteration = 0; iteration < iterations; iteration += 1) {
    const smoothed: GpsCoord[] = [];
    for (let index = 0; index < points.length; index += 1) {
      const current = points[index]!;
      const next = points[(index + 1) % points.length]!;
      smoothed.push({
        lat: current.lat + (next.lat - current.lat) * cornerCut,
        lon: current.lon + (next.lon - current.lon) * cornerCut,
      });
      smoothed.push({
        lat: current.lat + (next.lat - current.lat) * (1 - cornerCut),
        lon: current.lon + (next.lon - current.lon) * (1 - cornerCut),
      });
    }
    points = smoothed;
  }

  return points;
}

function rotateCoords(coords: GpsCoord[], offset: number) {
  const wrapped = ((offset % coords.length) + coords.length) % coords.length;
  return [...coords.slice(wrapped), ...coords.slice(0, wrapped)];
}

function alignRenderedRail(reference: GpsCoord[], candidate: GpsCoord[]) {
  let best = candidate;
  let bestScore = Number.POSITIVE_INFINITY;
  for (const oriented of [candidate, [...candidate].reverse()]) {
    for (let offset = 0; offset < oriented.length; offset += 1) {
      const score = haversineM(reference[0]!, oriented[offset]!)
        + haversineM(reference[1]!, oriented[(offset + 1) % oriented.length]!);
      if (score < bestScore) {
        bestScore = score;
        best = rotateCoords(oriented, offset);
      }
    }
  }
  return best;
}

function pointInRing(point: GpsCoord, ring: GpsCoord[]) {
  let inside = false;
  for (let currentIndex = 0, previousIndex = ring.length - 1; currentIndex < ring.length; previousIndex = currentIndex, currentIndex += 1) {
    const current = ring[currentIndex]!;
    const previous = ring[previousIndex]!;
    const intersects = (current.lat > point.lat) !== (previous.lat > point.lat)
      && point.lon < ((previous.lon - current.lon) * (point.lat - current.lat))
        / (previous.lat - current.lat) + current.lon;
    if (intersects) {
      inside = !inside;
    }
  }
  return inside;
}

function trackSurfaceContains(point: GpsCoord, outside: GpsCoord[], inside: GpsCoord[]) {
  return pointInRing(point, outside) && !pointInRing(point, inside);
}

function alignClosedRail(
  reference: GpsCoord[],
  candidate: GpsCoord[],
  containsPoint: (point: GpsCoord) => boolean,
) {
  let best = candidate;
  let bestScore = Number.POSITIVE_INFINITY;
  for (const oriented of [candidate, [...candidate].reverse()]) {
    for (let offset = 0; offset < oriented.length; offset += 1) {
      let score = 0;
      for (let index = 0; index < reference.length; index += 1) {
        const outsidePoint = reference[index]!;
        const insidePoint = oriented[(index + offset) % oriented.length]!;
        const midpoint = {
          lon: (outsidePoint.lon + insidePoint.lon) / 2,
          lat: (outsidePoint.lat + insidePoint.lat) / 2,
        };
        score += haversineM(outsidePoint, insidePoint)
          + (containsPoint(midpoint) ? 0 : 1_000_000);
      }
      if (score < bestScore) {
        bestScore = score;
        best = rotateCoords(oriented, offset);
      }
    }
  }
  return best;
}

function alignedRailPath(
  outside: GpsCoord[],
  inside: GpsCoord[],
  containsPoint: (point: GpsCoord) => boolean,
) {
  const rows = outside.length;
  const columns = inside.length;
  const costs = Array.from({ length: rows }, () => Array<number>(columns).fill(Number.POSITIVE_INFINITY));
  const previous = Array.from({ length: rows }, () => Array<0 | 1 | 2 | null>(columns).fill(null));
  const midpointAt = (outsideIndex: number, insideIndex: number) => ({
    lat: (outside[outsideIndex]!.lat + inside[insideIndex]!.lat) / 2,
    lon: (outside[outsideIndex]!.lon + inside[insideIndex]!.lon) / 2,
  });
  const transitionInsideSurface = (
    fromOutsideIndex: number,
    fromInsideIndex: number,
    toOutsideIndex: number,
    toInsideIndex: number,
  ) => {
    const from = midpointAt(fromOutsideIndex, fromInsideIndex);
    const to = midpointAt(toOutsideIndex, toInsideIndex);
    const outsideStep = haversineM(outside[fromOutsideIndex]!, outside[toOutsideIndex]!);
    const insideStep = haversineM(inside[fromInsideIndex]!, inside[toInsideIndex]!);
    const maxCenterStep = Math.max(8, Math.max(outsideStep, insideStep) * 2.5);
    return haversineM(from, to) <= maxCenterStep
      && [0.25, 0.5, 0.75].every((fraction) => containsPoint({
        lon: from.lon + (to.lon - from.lon) * fraction,
        lat: from.lat + (to.lat - from.lat) * fraction,
      }));
  };
  for (let outsideIndex = 0; outsideIndex < rows; outsideIndex += 1) {
    for (let insideIndex = 0; insideIndex < columns; insideIndex += 1) {
      const outsidePoint = outside[outsideIndex]!;
      const insidePoint = inside[insideIndex]!;
      const midpoint = {
        lon: (outsidePoint.lon + insidePoint.lon) / 2,
        lat: (outsidePoint.lat + insidePoint.lat) / 2,
      };
      const outsideSurface = !containsPoint(midpoint);
      if (outsideSurface) {
        continue;
      }
      const pairCost = haversineM(outsidePoint, insidePoint);
      if (outsideIndex === 0 && insideIndex === 0) {
        costs[outsideIndex]![insideIndex] = pairCost;
        continue;
      }

      const diagonal = outsideIndex > 0 && insideIndex > 0
        && transitionInsideSurface(outsideIndex - 1, insideIndex - 1, outsideIndex, insideIndex)
        ? costs[outsideIndex - 1]![insideIndex - 1]!
        : Number.POSITIVE_INFINITY;
      const advanceOutside = outsideIndex > 0
        && transitionInsideSurface(outsideIndex - 1, insideIndex, outsideIndex, insideIndex)
        ? costs[outsideIndex - 1]![insideIndex]! + pairCost * 0.15
        : Number.POSITIVE_INFINITY;
      const advanceInside = insideIndex > 0
        && transitionInsideSurface(outsideIndex, insideIndex - 1, outsideIndex, insideIndex)
        ? costs[outsideIndex]![insideIndex - 1]! + pairCost * 0.15
        : Number.POSITIVE_INFINITY;
      const best = Math.min(diagonal, advanceOutside, advanceInside);
      if (!Number.isFinite(best)) {
        continue;
      }
      costs[outsideIndex]![insideIndex] = pairCost + best;
      previous[outsideIndex]![insideIndex] = best === diagonal ? 0 : best === advanceOutside ? 1 : 2;
    }
  }

  const pairs: Array<[number, number]> = [];
  let outsideIndex = rows - 1;
  let insideIndex = columns - 1;
  while (insideIndex > 0 && !Number.isFinite(costs[outsideIndex]![insideIndex])) {
    insideIndex -= 1;
  }
  while (outsideIndex >= 0 && insideIndex >= 0) {
    pairs.push([outsideIndex, insideIndex]);
    const step = previous[outsideIndex]![insideIndex];
    if (step === null) {
      break;
    }
    if (step === 0 || step === 1) {
      outsideIndex -= 1;
    }
    if (step === 0 || step === 2) {
      insideIndex -= 1;
    }
  }

  const orderedPairs = pairs.reverse();
  return {
    outsideLine: orderedPairs.map(([outsidePointIndex]) => outside[outsidePointIndex]!),
    insideLine: orderedPairs.map(([, insidePointIndex]) => inside[insidePointIndex]!),
    centerLine: orderedPairs.map(([outsidePointIndex, insidePointIndex]) =>
      midpointAt(outsidePointIndex, insidePointIndex),
    ),
  };
}

function closeContributionRing(ring: TrackContributionGpsCoord[]) {
  if (ring.length < 3) {
    return ring;
  }
  const first = ring[0]!;
  const last = ring.at(-1)!;
  return first.lat === last.lat && first.lon === last.lon ? ring : [...ring, first];
}

function rewindPolygon(polygon: ReturnType<typeof turf.polygon>) {
  return turf.rewind(polygon, { reverse: false }) as ReturnType<typeof turf.polygon>;
}

function authoredSurfacePolygon(surface: AuthoredTrackSurface) {
  const polygon = turf.polygon(surface.outlineRings.map((ring) =>
    closeContributionRing(ring).map((coord) => [coord.lon, coord.lat]),
  ));
  return rewindPolygon(polygon);
}

function clippedFeaturePolygons(
  rings: TrackContributionGpsCoord[][],
  surface: AuthoredTrackSurface | null | undefined,
) {
  if (!surface || rings.length === 0) {
    return rings.length > 0 ? [rings] : [];
  }

  const featurePolygon = turf.polygon(rings.map((ring) =>
    closeContributionRing(ring).map((coord) => [coord.lon, coord.lat]),
  ));
  const clipped = turf.intersect(turf.featureCollection([
    rewindPolygon(featurePolygon),
    authoredSurfacePolygon(surface),
  ]));
  if (!clipped) {
    return [];
  }

  const polygons = clipped.geometry.type === 'Polygon'
    ? [clipped.geometry.coordinates]
    : clipped.geometry.coordinates;
  return polygons.map((polygon) =>
    polygon.map((ring) => ringFromTurfPositions(ring)),
  );
}

function authoredSurfaceSectionPolygons(
  surface: AuthoredTrackSurface,
  startFraction: number,
  endFraction: number,
  entryLine?: GpsCoord[],
  exitLine?: GpsCoord[],
) {
  const nearestRailDistance = (point: GpsCoord, rail: GpsCoord[]) =>
    Math.min(...rail.map((railPoint) => haversineM(point, railPoint)));
  const orientLine = (line: GpsCoord[] | undefined, outside: GpsCoord, inside: GpsCoord) => {
    if (line?.length !== 2) {
      return { outside, inside };
    }
    const direct = nearestRailDistance(line[0]!, surface.matchedOutsideLine)
      + nearestRailDistance(line[1]!, surface.matchedInsideLine);
    const reversed = nearestRailDistance(line[1]!, surface.matchedOutsideLine)
      + nearestRailDistance(line[0]!, surface.matchedInsideLine);
    return direct <= reversed
      ? { outside: line[0]!, inside: line[1]! }
      : { outside: line[1]!, inside: line[0]! };
  };
  const orientedEntry = orientLine(
    entryLine,
    coordAtMatchedFraction(surface.matchedOutsideLine, startFraction)!,
    coordAtMatchedFraction(surface.matchedInsideLine, startFraction)!,
  );
  const orientedExit = orientLine(
    exitLine,
    coordAtMatchedFraction(surface.matchedOutsideLine, endFraction)!,
    coordAtMatchedFraction(surface.matchedInsideLine, endFraction)!,
  );
  const matchedFractionAtLine = (line: { outside: GpsCoord; inside: GpsCoord }) => {
    const pointCount = Math.min(surface.matchedOutsideLine.length, surface.matchedInsideLine.length);
    const latitudeScale = 111_320;
    const longitudeScale = latitudeScale * Math.cos(line.outside.lat * Math.PI / 180);
    let bestFraction = 0;
    let bestError = Number.POSITIVE_INFINITY;
    for (let index = 0; index < pointCount; index += 1) {
      const nextIndex = (index + 1) % pointCount;
      const outsideStart = surface.matchedOutsideLine[index]!;
      const outsideEnd = surface.matchedOutsideLine[nextIndex]!;
      const insideStart = surface.matchedInsideLine[index]!;
      const insideEnd = surface.matchedInsideLine[nextIndex]!;
      const vectors = [
        {
          x: (outsideEnd.lon - outsideStart.lon) * longitudeScale,
          y: (outsideEnd.lat - outsideStart.lat) * latitudeScale,
          targetX: (line.outside.lon - outsideStart.lon) * longitudeScale,
          targetY: (line.outside.lat - outsideStart.lat) * latitudeScale,
        },
        {
          x: (insideEnd.lon - insideStart.lon) * longitudeScale,
          y: (insideEnd.lat - insideStart.lat) * latitudeScale,
          targetX: (line.inside.lon - insideStart.lon) * longitudeScale,
          targetY: (line.inside.lat - insideStart.lat) * latitudeScale,
        },
      ];
      const denominator = vectors.reduce((sum, vector) => sum + vector.x ** 2 + vector.y ** 2, 0);
      const amount = denominator > 0
        ? Math.max(0, Math.min(1, vectors.reduce((sum, vector) =>
          sum + vector.x * vector.targetX + vector.y * vector.targetY, 0) / denominator))
        : 0;
      const error = vectors.reduce((sum, vector) => {
        const deltaX = vector.targetX - vector.x * amount;
        const deltaY = vector.targetY - vector.y * amount;
        return sum + deltaX ** 2 + deltaY ** 2;
      }, 0);
      if (error < bestError) {
        bestError = error;
        bestFraction = (index + amount) / pointCount;
      }
    }
    return bestFraction;
  };
  const clipLineToRails = (
    sourceLine: GpsCoord[] | undefined,
    oriented: { outside: GpsCoord; inside: GpsCoord },
  ) => {
    const projectedFraction = matchedFractionAtLine(oriented);
    if (sourceLine?.length !== 2) {
      return {
        outside: coordAtMatchedFraction(surface.matchedOutsideLine, projectedFraction)!,
        inside: coordAtMatchedFraction(surface.matchedInsideLine, projectedFraction)!,
      };
    }
    const boundary = turf.lineString(sourceLine.map((point) => [point.lon, point.lat]));
    const intersectionOnRail = (rail: GpsCoord[], target: GpsCoord) => {
      const closedRail = [...rail, rail[0]!];
      const intersections = turf.lineIntersect(
        boundary,
        turf.lineString(closedRail.map((point) => [point.lon, point.lat])),
      ).features;
      const nearest = intersections.reduce<(typeof intersections)[number] | null>((best, candidate) => {
        if (!best) {
          return candidate;
        }
        const candidateCoord = { lat: candidate.geometry.coordinates[1]!, lon: candidate.geometry.coordinates[0]! };
        const bestCoord = { lat: best.geometry.coordinates[1]!, lon: best.geometry.coordinates[0]! };
        return haversineM(candidateCoord, target) < haversineM(bestCoord, target) ? candidate : best;
      }, null);
      return nearest
        ? { lat: nearest.geometry.coordinates[1]!, lon: nearest.geometry.coordinates[0]! }
        : null;
    };
    return {
      outside: intersectionOnRail(surface.matchedOutsideLine, oriented.outside)
        ?? coordAtMatchedFraction(surface.matchedOutsideLine, projectedFraction)!,
      inside: intersectionOnRail(surface.matchedInsideLine, oriented.inside)
        ?? coordAtMatchedFraction(surface.matchedInsideLine, projectedFraction)!,
    };
  };
  const entry = clipLineToRails(entryLine, orientedEntry);
  const exit = clipLineToRails(exitLine, orientedExit);
  const railStartFraction = entryLine?.length === 2 ? matchedFractionAtLine(entry) : startFraction;
  const railEndFraction = exitLine?.length === 2 ? matchedFractionAtLine(exit) : endFraction;
  const outsideArc = extractMatchedRailArc(
    surface.matchedOutsideLine,
    railStartFraction,
    railEndFraction,
  );
  const insideArc = extractMatchedRailArc(
    surface.matchedInsideLine,
    railStartFraction,
    railEndFraction,
  );
  if (outsideArc.length < 2 || insideArc.length < 2) {
    return [];
  }
  outsideArc[0] = entry.outside;
  insideArc[0] = entry.inside;
  outsideArc[outsideArc.length - 1] = exit.outside;
  insideArc[insideArc.length - 1] = exit.inside;

  const quads = outsideArc.slice(1).map((outsidePoint, index) => turf.polygon([[
    [outsideArc[index]!.lon, outsideArc[index]!.lat],
    [outsidePoint.lon, outsidePoint.lat],
    [insideArc[index + 1]!.lon, insideArc[index + 1]!.lat],
    [insideArc[index]!.lon, insideArc[index]!.lat],
    [outsideArc[index]!.lon, outsideArc[index]!.lat],
  ]]));
  const unified = turf.union(turf.featureCollection(quads));
  if (!unified) {
    return [];
  }
  const polygons = unified.geometry.type === 'Polygon'
    ? [unified.geometry.coordinates]
    : unified.geometry.coordinates;
  return polygons.map((polygon) => polygon.map((ring) => ringFromTurfPositions(ring)));
}

function coordAtMatchedFraction(points: GpsCoord[], fraction: number) {
  if (points.length === 0) {
    return null;
  }
  const position = normalizeFraction(fraction) * points.length;
  const index = Math.floor(position) % points.length;
  const nextIndex = (index + 1) % points.length;
  const amount = position - Math.floor(position);
  const current = points[index]!;
  const next = points[nextIndex]!;
  return {
    lat: current.lat + (next.lat - current.lat) * amount,
    lon: current.lon + (next.lon - current.lon) * amount,
  };
}

function extractMatchedRailArc(points: GpsCoord[], startFraction: number, endFraction: number) {
  const span = sectionSpan(startFraction, endFraction);
  const sampleCount = Math.max(8, Math.ceil(points.length * span));
  return Array.from({ length: sampleCount + 1 }, (_, index) =>
    coordAtMatchedFraction(points, startFraction + (span * index) / sampleCount)!,
  );
}

export function authoredSurfaceDividerAtFraction(
  surface: AuthoredTrackSurface,
  fraction: number,
) {
  const outside = coordAtMatchedFraction(surface.matchedOutsideLine, fraction);
  const inside = coordAtMatchedFraction(surface.matchedInsideLine, fraction);
  return outside && inside ? { pointA: outside, pointB: inside } : null;
}

export function buildTurnCoordinatePacket(
  feature: DetailedCircuitFeature,
  section: CatalogLapSection,
  corner: CornerApexSuggestion,
  surface: AuthoredTrackSurface,
  kerbLines: GpsCoord[][],
) {
  const polygonRings = feature.polygonRings ?? [];
  if (polygonRings.length === 0) {
    return null;
  }
  const polygon = turf.polygon(polygonRings.map((ring) =>
    closeContributionRing(ring).map((point) => [point.lon, point.lat]),
  ));
  const tolerance = turf.buffer(polygon, 1, { units: 'meters' }) ?? polygon;
  const pointBelongsToTurn = (point: GpsCoord) => turf.booleanPointInPolygon(
    turf.point([point.lon, point.lat]),
    tolerance,
    { ignoreBoundary: false },
  );
  const railDistanceM = (point: GpsCoord, rail: GpsCoord[]) => turf.pointToLineDistance(
    turf.point([point.lon, point.lat]),
    turf.lineString([...rail, rail[0]!].map((coord) => [coord.lon, coord.lat])),
    { units: 'meters' },
  );
  const kerbs = kerbLines.flatMap((coordinates, index) => {
    if (!coordinates.some(pointBelongsToTurn)) {
      return [];
    }
    const midpoint = coordinates[Math.floor(coordinates.length / 2)]!;
    const outsideDistanceM = railDistanceM(midpoint, surface.outsideLine);
    const insideDistanceM = railDistanceM(midpoint, surface.insideLine);
    return [{
      id: `kerb-${index + 1}`,
      side: outsideDistanceM <= insideDistanceM ? 'outside' : 'inside',
      coordinates: coordinates.map(toContributionCoord),
      midpoint: toContributionCoord(midpoint),
      distanceToApexM: haversineM(midpoint, corner),
    }];
  });

  return {
    schemaVersion: 1,
    turn: {
      id: corner.id,
      turnNumber: corner.turnNumber,
      label: feature.label,
      apex: toContributionCoord(corner),
      confidence: corner.confidence,
    },
    bounds: {
      startFraction: section.startFraction,
      endFraction: section.endFraction,
      entryLine: feature.entryLine ?? section.entryLine?.map(toContributionCoord) ?? [],
      exitLine: feature.exitLine ?? section.exitLine?.map(toContributionCoord) ?? [],
    },
    trackSurface: {
      polygonRings,
      outsideRailCoordinates: surface.matchedOutsideLine.filter(pointBelongsToTurn).map(toContributionCoord),
      insideRailCoordinates: surface.matchedInsideLine.filter(pointBelongsToTurn).map(toContributionCoord),
    },
    kerbs,
  };
}

export function buildAuthoredTrackSurface(
  outsideSource: GpsCoord[],
  insideSource: GpsCoord[],
): AuthoredTrackSurface | null {
  const outsideOpen = smoothClosedRail(outsideSource);
  const insideOpen = smoothClosedRail(insideSource);
  if (outsideOpen.length < 3 || insideOpen.length < 3) {
    return null;
  }

  const sourcePointCount = Math.max(outsideSource.length, insideSource.length);
  const alignmentSampleCount = Math.max(80, Math.min(180, sourcePointCount));
  const renderSampleCount = Math.max(320, Math.min(720, sourcePointCount * 4));
  const alignmentOutsideLine = resampleClosedPolyline(outsideOpen, alignmentSampleCount);
  const alignmentInsideLine = resampleClosedPolyline(insideOpen, alignmentSampleCount);
  const containsPoint = (point: GpsCoord) =>
    trackSurfaceContains(point, alignmentOutsideLine, alignmentInsideLine);
  const alignedInsideLine = alignClosedRail(
    alignmentOutsideLine,
    alignmentInsideLine,
    containsPoint,
  );
  const matchedPath = alignedRailPath(alignmentOutsideLine, alignedInsideLine, containsPoint);
  const outsideLine = resampleClosedPolyline(outsideOpen, renderSampleCount);
  const insideLine = alignRenderedRail(
    alignedInsideLine,
    resampleClosedPolyline(insideOpen, renderSampleCount),
  );
  const centerLine = resampleClosedPolyline(
    smoothClosedRail(matchedPath.centerLine),
    renderSampleCount,
  );
  const surfacePolygon = rewindPolygon(turf.polygon([
    closeContributionRing(outsideLine.map(toContributionCoord)).map((coord) => [coord.lon, coord.lat]),
    closeContributionRing(insideLine.map(toContributionCoord)).map((coord) => [coord.lon, coord.lat]),
  ]));

  return {
    outsideLine,
    insideLine,
    matchedOutsideLine: matchedPath.outsideLine,
    matchedInsideLine: matchedPath.insideLine,
    centerLine: [...centerLine, centerLine[0]!],
    outlineRings: surfacePolygon.geometry.coordinates.map((ring) => ringFromTurfPositions(ring)),
  };
}

export function buildDistanceMarkers(
  sourcePolyline: GpsCoord[],
  intervalM = 160.9344,
): Array<{ distanceM: number; coord: GpsCoord }> {
  const polyline = healCircuitPolylineForDisplay(sourcePolyline);
  if (polyline.length < 2 || intervalM <= 0) {
    return [];
  }

  const cumulative = buildCumulativeDistances(polyline);
  const total = cumulative.at(-1) ?? 0;
  const markers: Array<{ distanceM: number; coord: GpsCoord }> = [];
  for (let distanceM = intervalM; distanceM < total; distanceM += intervalM) {
    markers.push({
      distanceM,
      coord: coordAtDistance(polyline, cumulative, distanceM),
    });
  }
  return markers;
}

function extractPolylineArc(polyline: GpsCoord[], startFraction: number, endFraction: number): GpsCoord[] {
  const open = openPolyline(polyline);
  if (open.length < 2) {
    return open;
  }

  const cumulative = buildCumulativeDistances(open);
  const total = cumulative.at(-1) ?? 1;
  const startDist = normalizeFraction(startFraction) * total;
  let endDist = normalizeFraction(endFraction) * total;
  if (endDist <= startDist) {
    endDist += total;
  }

  const arcLength = endDist - startDist;
  if (arcLength <= 0.5) {
    return [];
  }

  const result: GpsCoord[] = [];
  const sampleCount = Math.max(8, Math.ceil(arcLength / 4));
  for (let sample = 0; sample <= sampleCount; sample += 1) {
    const distance = startDist + (arcLength * sample) / sampleCount;
    result.push(coordAtDistance(open, cumulative, distance));
  }

  return result.filter((coord, index) =>
    index === 0 || haversineM(result[index - 1]!, coord) > 0.2,
  );
}

function startLineToEntryExit(startLine: StartLineSuggestion) {
  return {
    entry: [toContributionCoord(startLine.pointA), toContributionCoord(startLine.pointB)],
    exit: [toContributionCoord(startLine.pointA), toContributionCoord(startLine.pointB)],
  };
}

function sectionEntryExitLines(
  polyline: GpsCoord[],
  section: LapSection,
  trackWidthM: number,
  authoredSurface?: AuthoredTrackSurface | null,
) {
  if (section.entryLine?.length === 2 && section.exitLine?.length === 2) {
    return {
      entryLine: section.entryLine.map(toContributionCoord),
      exitLine: section.exitLine.map(toContributionCoord),
    };
  }

  if (authoredSurface) {
    const lineAtFraction = (fraction: number) => {
      const outside = coordAtPolylineFraction(authoredSurface.outsideLine, fraction);
      const inside = coordAtPolylineFraction(authoredSurface.insideLine, fraction);
      return outside && inside
        ? [toContributionCoord(outside), toContributionCoord(inside)]
        : [];
    };
    return {
      entryLine: lineAtFraction(section.startFraction),
      exitLine: lineAtFraction(section.endFraction),
    };
  }

  const dividerWidthM = catalogDividerWidthM(trackWidthM);
  const entryLine = buildStartLineAtFraction(polyline, normalizeFraction(section.startFraction), dividerWidthM);
  const exitLine = buildStartLineAtFraction(polyline, normalizeFraction(section.endFraction), dividerWidthM);
  return {
    entryLine: [toContributionCoord(entryLine.pointA), toContributionCoord(entryLine.pointB)],
    exitLine: [toContributionCoord(exitLine.pointA), toContributionCoord(exitLine.pointB)],
  };
}

function sectionSpan(startFraction: number, endFraction: number) {
  const start = normalizeFraction(startFraction);
  let end = normalizeFraction(endFraction);
  if (end <= start) {
    end += 1;
  }
  return end - start;
}

function fractionDistance(left: number, right: number) {
  const diff = Math.abs(normalizeFraction(left) - normalizeFraction(right));
  return Math.min(diff, 1 - diff);
}

function nextCatalogSectionId(type: 'straight' | 'corner', sections: CatalogLapSection[]) {
  if (type === 'straight') {
    const straightCount = sections.filter((section) => section.type === 'straight').length;
    return `straight-${straightCount + 1}`;
  }

  const cornerCount = sections.filter((section) => section.type === 'corner').length;
  return `corner-${cornerCount + 1}`;
}

function internalLapSections(
  catalogSections: CatalogLapSection[],
  corners: CornerApexSuggestion[],
): LapSection[] {
  const cornerById = new Map(corners.map((corner) => [corner.id, corner]));
  return catalogSections.map((section) => ({
    type: section.type,
    startFraction: section.startFraction,
    endFraction: section.endFraction,
    corner: section.cornerId ? cornerById.get(section.cornerId) : undefined,
    entryLine: section.entryLine,
    exitLine: section.exitLine,
  }));
}

export function detectCatalogLapSections(
  polyline: GpsCoord[],
  corners: CornerApexSuggestion[],
  options?: { cornerArcHalfM?: number },
): CatalogLapSection[] {
  const cornerArcHalfM = options?.cornerArcHalfM ?? 18;
  const internal = rotateSectionsToStart(buildLapSections(polyline, corners, cornerArcHalfM));
  let straightNumber = 1;
  const sections: CatalogLapSection[] = internal.map((section) => ({
    id: section.type === 'straight'
      ? `straight-${straightNumber++}`
      : (section.corner?.id ?? `corner-${straightNumber}`),
    type: section.type,
    startFraction: section.startFraction,
    endFraction: section.endFraction,
    cornerId: section.corner?.id,
  }));

  for (let index = 0; index < sections.length; index += 1) {
    const next = sections[(index + 1) % sections.length]!;
    sections[index]!.endFraction = next.startFraction;
  }

  return sections;
}

/** Rebuild straight/corner sections after the corner list changes (add/remove turns). */
export function rebuildCatalogLapSections(
  polyline: GpsCoord[],
  corners: CornerApexSuggestion[],
  options?: { cornerArcHalfM?: number },
): CatalogLapSection[] {
  if (corners.length === 0) {
    return [{
      id: 'straight-1',
      type: 'straight',
      startFraction: 0,
      endFraction: 1,
    }];
  }

  return detectCatalogLapSections(polyline, corners, options);
}

export function nearestCatalogLapSectionBoundaryIndex(
  sections: CatalogLapSection[],
  fraction: number,
  tolerance = 0.012,
): number | null {
  if (sections.length === 0) {
    return null;
  }

  let bestIndex: number | null = null;
  let bestDistance = tolerance;
  for (let index = 0; index < sections.length; index += 1) {
    const distance = fractionDistance(fraction, sections[index]!.endFraction);
    if (distance <= bestDistance) {
      bestDistance = distance;
      bestIndex = index;
    }
  }

  return bestIndex;
}

function isFractionInForwardRange(
  fraction: number,
  rangeStart: number,
  rangeEnd: number,
  margin = 0.0001,
): boolean {
  const span = sectionSpan(rangeStart, rangeEnd);
  if (span <= margin * 2) {
    return false;
  }

  const offset = sectionSpan(rangeStart, normalizeFraction(fraction));
  return offset >= margin && offset <= span - margin;
}

function sortSectionsAroundLap(sections: CatalogLapSection[]): CatalogLapSection[] {
  return [...sections].sort((left, right) =>
    normalizeFraction(left.startFraction) - normalizeFraction(right.startFraction),
  );
}

/** Replace overlapping sections with a straight or corner range marked between two clicks. */
export function upsertCatalogLapSectionRange(
  sections: CatalogLapSection[],
  type: 'straight' | 'corner',
  startFraction: number,
  endFraction: number,
  options?: {
    minSpan?: number;
    cornerId?: string;
    entryLine?: GpsCoord[];
    exitLine?: GpsCoord[];
  },
): CatalogLapSection[] | null {
  const minSpan = options?.minSpan ?? 0.008;
  const start = normalizeFraction(startFraction);
  const end = normalizeFraction(endFraction);

  if (fractionDistance(start, end) < minSpan) {
    return null;
  }

  const span = sectionSpan(start, end);
  if (span < minSpan) {
    return null;
  }

  const newSection: CatalogLapSection = {
    id: nextCatalogSectionId(type, sections),
    type,
    startFraction: start,
    endFraction: end,
    cornerId: type === 'corner' ? options?.cornerId : undefined,
    entryLine: type === 'corner' ? options?.entryLine : undefined,
    exitLine: type === 'corner' ? options?.exitLine : undefined,
  };

  const boundaries = [...sections.map((section) => normalizeFraction(section.startFraction)), start, end]
    .filter((fraction) => !isFractionInForwardRange(fraction, start, end))
    .sort((left, right) => left - right)
    .filter((fraction, index, sorted) => index === 0 || fractionDistance(fraction, sorted[index - 1]!) > 0.0001);

  type SectionFragment = CatalogLapSection & { sourceId: string | null };
  const fragments = boundaries.map((fragmentStart, index): SectionFragment => {
    const fragmentEnd = boundaries[(index + 1) % boundaries.length]!;
    if (fractionDistance(fragmentStart, start) <= 0.0001) {
      return { ...newSection, startFraction: fragmentStart, endFraction: fragmentEnd, sourceId: null };
    }

    const midpoint = normalizeFraction(fragmentStart + sectionSpan(fragmentStart, fragmentEnd) / 2);
    const source = sections.find((section) => {
      const offset = sectionSpan(section.startFraction, midpoint);
      return offset < sectionSpan(section.startFraction, section.endFraction) - 0.0001;
    }) ?? sections[0]!;
    const unchanged = fractionDistance(fragmentStart, source.startFraction) <= 0.0001
      && fractionDistance(fragmentEnd, source.endFraction) <= 0.0001;
    return {
      ...source,
      startFraction: fragmentStart,
      endFraction: fragmentEnd,
      entryLine: unchanged ? source.entryLine : undefined,
      exitLine: unchanged ? source.exitLine : undefined,
      sourceId: source.id,
    };
  });

  const merged = fragments.reduce<SectionFragment[]>((result, fragment) => {
    const previous = result.at(-1);
    if (previous?.sourceId && previous.sourceId === fragment.sourceId) {
      previous.endFraction = fragment.endFraction;
      return result;
    }
    result.push({ ...fragment });
    return result;
  }, []);
  const first = merged[0];
  const last = merged.at(-1);
  if (merged.length > 1 && first?.sourceId && first.sourceId === last?.sourceId) {
    last.endFraction = first.endFraction;
    merged.shift();
  }

  return merged.map(({ sourceId: _sourceId, ...section }) => section);
}

export function insertCatalogLapSectionBoundary(
  sections: CatalogLapSection[],
  fraction: number,
  minSpan = 0.008,
): CatalogLapSection[] | null {
  if (sections.length === 0) {
    return null;
  }

  const nextFraction = normalizeFraction(fraction);
  if (nearestCatalogLapSectionBoundaryIndex(sections, nextFraction, minSpan) !== null) {
    return null;
  }

  let sectionIndex = -1;
  for (let index = 0; index < sections.length; index += 1) {
    const section = sections[index]!;
    const span = sectionSpan(section.startFraction, section.endFraction);
    const offset = sectionSpan(section.startFraction, nextFraction);
    if (offset >= minSpan && span - offset >= minSpan) {
      sectionIndex = index;
      break;
    }
  }

  if (sectionIndex < 0) {
    return null;
  }

  const current = sections[sectionIndex]!;
  const oppositeType = current.type === 'straight' ? 'corner' : 'straight';
  const first: CatalogLapSection = {
    ...current,
    id: `${current.id}-a`,
    endFraction: nextFraction,
  };
  const second: CatalogLapSection = {
    id: nextCatalogSectionId(oppositeType, sections),
    type: oppositeType,
    startFraction: nextFraction,
    endFraction: current.endFraction,
    cornerId: oppositeType === 'corner' ? current.cornerId : undefined,
  };

  if (current.type === 'corner') {
    first.cornerId = current.cornerId;
  }

  return syncCatalogLapSectionFractions([
    ...sections.slice(0, sectionIndex),
    first,
    second,
    ...sections.slice(sectionIndex + 1),
  ]);
}

export function deleteCatalogLapSectionBoundary(
  sections: CatalogLapSection[],
  boundaryIndex: number,
): CatalogLapSection[] | null {
  if (sections.length <= 1) {
    return null;
  }

  const wrappedIndex = ((boundaryIndex % sections.length) + sections.length) % sections.length;
  const nextIndex = (wrappedIndex + 1) % sections.length;
  const left = sections[wrappedIndex]!;
  const right = sections[nextIndex]!;
  const mergedType = left.type === 'corner' || right.type === 'corner' ? 'corner' : 'straight';
  const merged: CatalogLapSection = {
    id: mergedType === 'corner'
      ? (left.cornerId ?? right.cornerId ?? left.id)
      : left.id,
    type: mergedType,
    startFraction: left.startFraction,
    endFraction: right.endFraction,
    cornerId: left.type === 'corner'
      ? left.cornerId
      : right.type === 'corner'
        ? right.cornerId
        : undefined,
  };

  const nextSections = [
    ...sections.slice(0, wrappedIndex),
    merged,
    ...sections.slice(nextIndex + 1),
  ];

  return syncCatalogLapSectionFractions(nextSections);
}

export function updateCatalogLapSectionBoundary(
  sections: CatalogLapSection[],
  boundaryIndex: number,
  fraction: number,
  minSpan = 0.008,
): CatalogLapSection[] {
  if (sections.length < 2) {
    return sections;
  }

  const wrappedIndex = ((boundaryIndex % sections.length) + sections.length) % sections.length;
  const nextIndex = (wrappedIndex + 1) % sections.length;
  const nextFraction = normalizeFraction(fraction);
  const current = sections[wrappedIndex]!;
  const next = sections[nextIndex]!;

  if (sectionSpan(current.startFraction, nextFraction) < minSpan) {
    return sections;
  }
  if (sectionSpan(nextFraction, next.endFraction) < minSpan) {
    return sections;
  }

  return sections.map((section, index) => {
    if (index === wrappedIndex) {
      return {
        ...section,
        endFraction: nextFraction,
        exitLine: section.type === 'corner' ? undefined : section.exitLine,
      };
    }
    if (index === nextIndex) {
      return {
        ...section,
        startFraction: nextFraction,
        entryLine: section.type === 'corner' ? undefined : section.entryLine,
      };
    }
    return section;
  });
}

export function syncCatalogLapSectionFractions(sections: CatalogLapSection[]): CatalogLapSection[] {
  if (sections.length === 0) {
    return sections;
  }

  return sections.map((section, index) => {
    const next = sections[(index + 1) % sections.length]!;
    return { ...section, endFraction: next.startFraction };
  });
}

export function canDeleteCatalogLapStraightSection(
  sections: CatalogLapSection[],
  straightIndex: number,
): boolean {
  const current = sections[straightIndex];
  if (!current || current.type !== 'straight' || sections.length < 2) {
    return false;
  }

  const prev = sections[(straightIndex - 1 + sections.length) % sections.length]!;
  const next = sections[(straightIndex + 1) % sections.length]!;
  return prev.type === 'straight' || next.type === 'straight';
}

export function splitCatalogLapStraightSection(
  sections: CatalogLapSection[],
  straightIndex: number,
  splitT = 0.5,
): CatalogLapSection[] | null {
  const current = sections[straightIndex];
  if (!current || current.type !== 'straight') {
    return null;
  }

  const clampedSplit = Math.min(0.95, Math.max(0.05, splitT));
  const span = sectionSpan(current.startFraction, current.endFraction);
  if (span <= 0.02) {
    return null;
  }

  const splitFraction = normalizeFraction(current.startFraction + span * clampedSplit);
  if (
    sectionSpan(current.startFraction, splitFraction) < 0.008
    || sectionSpan(splitFraction, current.endFraction) < 0.008
  ) {
    return null;
  }

  const first: CatalogLapSection = {
    ...current,
    id: `${current.id}-a`,
    endFraction: splitFraction,
  };
  const second: CatalogLapSection = {
    ...current,
    id: `${current.id}-b`,
    startFraction: splitFraction,
  };

  return syncCatalogLapSectionFractions([
    ...sections.slice(0, straightIndex),
    first,
    second,
    ...sections.slice(straightIndex + 1),
  ]);
}

export function deleteCatalogLapStraightSection(
  sections: CatalogLapSection[],
  straightIndex: number,
): CatalogLapSection[] | null {
  if (!canDeleteCatalogLapStraightSection(sections, straightIndex)) {
    return null;
  }

  const current = sections[straightIndex]!;
  const prevIndex = (straightIndex - 1 + sections.length) % sections.length;
  const nextIndex = (straightIndex + 1) % sections.length;
  const prev = sections[prevIndex]!;
  const next = sections[nextIndex]!;

  let nextSections: CatalogLapSection[];
  if (prev.type === 'straight') {
    nextSections = sections
      .map((section, index) => (
        index === prevIndex
          ? { ...section, endFraction: current.endFraction }
          : section
      ))
      .filter((_, index) => index !== straightIndex);
  } else if (next.type === 'straight') {
    nextSections = sections
      .map((section, index) => (
        index === straightIndex
          ? { ...section, endFraction: next.endFraction }
          : section
      ))
      .filter((_, index) => index !== nextIndex);
  } else {
    return null;
  }

  return syncCatalogLapSectionFractions(nextSections);
}

export function hasStraightBetweenEachCorner(sections: CatalogLapSection[]): boolean {
  if (sections.length === 0) {
    return true;
  }

  const cornerIndices = sections
    .map((section, index) => (section.type === 'corner' ? index : -1))
    .filter((index) => index >= 0);

  if (cornerIndices.length === 0) {
    return sections.some((section) => section.type === 'straight');
  }

  for (let cornerCursor = 0; cornerCursor < cornerIndices.length; cornerCursor += 1) {
    const startIndex = cornerIndices[cornerCursor]!;
    const endIndex = cornerIndices[(cornerCursor + 1) % cornerIndices.length]!;
    let cursor = (startIndex + 1) % sections.length;
    let straightCount = 0;

    while (cursor !== endIndex) {
      if (sections[cursor]?.type === 'straight') {
        straightCount += 1;
      }
      cursor = (cursor + 1) % sections.length;
      if (cursor === startIndex) {
        break;
      }
    }

    if (straightCount < 1) {
      return false;
    }
  }

  return true;
}

export function crossTrackBoundaryLines(
  polyline: GpsCoord[],
  sections: CatalogLapSection[],
  trackWidthM: number,
  authoredSurface?: AuthoredTrackSurface | null,
): CrossTrackBoundaryLine[] {
  if (sections.length === 0) {
    return [];
  }

  const dividerWidthM = catalogDividerWidthM(trackWidthM);

  return sections.flatMap((section, index) => {
    const nextSection = sections[(index + 1) % sections.length]!;
    if (section.type !== 'corner' && nextSection.type !== 'corner') {
      return [];
    }
    const fraction = section.endFraction;
    const generatedLine = buildStartLineAtFraction(polyline, fraction, dividerWidthM);
    const pointA = authoredSurface
      ? coordAtPolylineFraction(authoredSurface.outsideLine, fraction)
      : generatedLine.pointA;
    const pointB = authoredSurface
      ? coordAtPolylineFraction(authoredSurface.insideLine, fraction)
      : generatedLine.pointB;
    if (!pointA || !pointB) {
      return [];
    }
    const color = section.type === 'corner' || nextSection.type === 'corner'
      ? '#ff5f6d'
      : '#28c8ff';
    return [{
      id: `lap-boundary-${index}`,
      boundaryIndex: index,
      pointA,
      pointB,
      color,
    }];
  });
}

function centerlineCoordsForBuffer(sourcePolyline: GpsCoord[], closedCircuit = false) {
  const polyline = closedCircuit ? healCircuitPolylineForDisplay(sourcePolyline) : sourcePolyline;
  const open = openPolyline(polyline);
  if (open.length < 2) {
    return open;
  }

  if (!closedCircuit) {
    return open;
  }

  if (haversineM(open[0]!, open.at(-1)!) <= 2) {
    return open;
  }

  return [...open, open[0]!];
}

function nearestCornerToCoord(corners: CornerApexSuggestion[], coord: GpsCoord) {
  let best: CornerApexSuggestion | undefined;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const corner of corners) {
    const distance = haversineM(corner, coord);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = corner;
    }
  }
  return best;
}

function buildLapSections(
  polyline: GpsCoord[],
  corners: CornerApexSuggestion[],
  cornerArcHalfM: number,
): LapSection[] {
  const open = openPolyline(healCircuitPolylineForDisplay(polyline));
  const total = polylineLengthM(open);
  if (total <= 0 || corners.length === 0) {
    return [{ type: 'straight', startFraction: 0, endFraction: 1 }];
  }

  const cumulative = buildCumulativeDistances(open);
  const sampleCount = 240;
  const labels: Array<{ type: 'corner' | 'straight'; corner?: CornerApexSuggestion }> = [];

  for (let sample = 0; sample < sampleCount; sample += 1) {
    const distance = (sample / sampleCount) * total;
    const coord = coordAtDistance(open, cumulative, distance);
    const nearest = nearestCornerToCoord(corners, coord);
    const nearCorner = nearest && haversineM(nearest, coord) <= cornerArcHalfM;
    labels.push(
      nearCorner
        ? { type: 'corner', corner: nearest }
        : { type: 'straight' },
    );
  }

  const sections: LapSection[] = [];
  let runStart = 0;
  let run = labels[0]!;

  for (let sample = 1; sample <= sampleCount; sample += 1) {
    const current = labels[sample] ?? run;
    const typeChanged = current.type !== run.type;
    const cornerChanged = run.type === 'corner'
      && current.type === 'corner'
      && current.corner?.id !== run.corner?.id;

    if (sample === sampleCount || typeChanged || cornerChanged) {
      const endFraction = sample / sampleCount;
      if (endFraction - runStart / sampleCount >= 0.004) {
        sections.push({
          type: run.type,
          startFraction: runStart / sampleCount,
          endFraction,
          corner: run.type === 'corner' ? run.corner : undefined,
        });
      }
      runStart = sample;
      run = current;
    }
  }

  return rotateSectionsToStart(sections);
}

function rotateSectionsToStart(sections: LapSection[]): LapSection[] {
  if (sections.length === 0) {
    return sections;
  }

  const startIndex = sections.findIndex((section) => section.type === 'straight');
  if (startIndex <= 0) {
    return sections;
  }

  return [...sections.slice(startIndex), ...sections.slice(0, startIndex)];
}

function buildLapSectionFeatures(
  polyline: GpsCoord[],
  sections: LapSection[],
  trackWidthM: number,
  authoredSurface?: AuthoredTrackSurface | null,
): DetailedCircuitFeature[] {
  const halfWidth = trackWidthM / 2;
  const features: DetailedCircuitFeature[] = [];
  let straightNumber = 1;
  let parentId: string | null = null;

  for (const section of sections) {
    const arc = extractPolylineArc(polyline, section.startFraction, section.endFraction);
    if (arc.length < 2) {
      continue;
    }

    const clippedPolygons = authoredSurface
      ? authoredSurfaceSectionPolygons(
        authoredSurface,
        section.startFraction,
        section.endFraction,
        section.entryLine,
        section.exitLine,
      )
      : clippedFeaturePolygons(flatCapSectionRings(arc, halfWidth), null);
    if (clippedPolygons.length === 0) {
      continue;
    }

    const { entryLine, exitLine } = sectionEntryExitLines(
      polyline,
      section,
      trackWidthM,
      authoredSurface,
    );
    if (section.type === 'straight') {
      const id = `straight-${straightNumber}`;
      const feature: DetailedCircuitFeature = {
        id,
        parentId,
        type: 'Straight',
        label: `Straight ${straightNumber}`,
        polygonRings: clippedPolygons[0],
        entryLine,
        exitLine,
      };
      features.push(feature, ...clippedPolygons.slice(1).map((polygonRings, index) => ({
        ...feature,
        id: `${id}-part-${index + 2}`,
        polygonRings,
      })));
      parentId = id;
      straightNumber += 1;
      continue;
    }

    const turnNumber = section.corner?.turnNumber ?? features.filter((feature) => feature.type === 'Corner').length + 1;
    const id = section.corner?.id ?? `corner-${turnNumber}`;
    const feature: DetailedCircuitFeature = {
      id,
      parentId,
      type: 'Corner',
      label: section.corner?.label ?? `Turn ${turnNumber}`,
      polygonRings: clippedPolygons[0],
      entryLine,
      exitLine,
      apexPoint: section.corner
        ? toContributionCoord({ lat: section.corner.lat, lon: section.corner.lon })
        : null,
    };
    features.push(feature, ...clippedPolygons.slice(1).map((polygonRings, index) => ({
      ...feature,
      id: `${id}-part-${index + 2}`,
      polygonRings,
    })));
    parentId = id;
  }

  return features;
}

function buildSectorFeatures(
  polyline: GpsCoord[],
  segments: SegmentSuggestion[],
  trackWidthM: number,
  authoredSurface?: AuthoredTrackSurface | null,
): DetailedCircuitFeature[] {
  const halfWidth = trackWidthM / 2;
  return segments.flatMap((segment) => {
    const arc = extractPolylineArc(polyline, segment.startFraction, segment.endFraction);
    if (arc.length < 2) {
      return [];
    }

    const clippedPolygons = authoredSurface
      ? authoredSurfaceSectionPolygons(authoredSurface, segment.startFraction, segment.endFraction)
      : clippedFeaturePolygons(flatCapSectionRings(arc, halfWidth), null);
    if (clippedPolygons.length === 0) {
      return [];
    }

    const { entryLine, exitLine } = sectionEntryExitLines(
      polyline,
      { type: 'straight', startFraction: segment.startFraction, endFraction: segment.endFraction },
      trackWidthM,
      authoredSurface,
    );

    const feature: DetailedCircuitFeature = {
      id: segment.id,
      parentId: null,
      type: 'Sector',
      label: segment.label,
      polygonRings: clippedPolygons[0],
      entryLine,
      exitLine,
    };
    return [feature, ...clippedPolygons.slice(1).map((polygonRings, index) => ({
      ...feature,
      id: `${segment.id}-part-${index + 2}`,
      polygonRings,
    }))];
  });
}

export function buildDetailedGeometryFromCenterline(
  sourcePolyline: GpsCoord[],
  options: BuildDetailedGeometryOptions = {},
): DetailedCircuitGeometry {
  const trackWidthM = options.trackWidthM ?? 10;
  const cornerArcHalfM = options.cornerArcHalfM ?? 18;
  const polyline = healCircuitPolylineForDisplay(sourcePolyline);
  const centerLine = (options.authoredSurface?.centerLine ?? polyline).map(toContributionCoord);
  const halfWidth = trackWidthM / 2;
  const outlineRings = options.authoredSurface?.outlineRings
    ?? bufferPolylineToRings(centerlineCoordsForBuffer(polyline, true), halfWidth);
  const corners = options.corners ?? detectCornersFromPolyline(polyline);
  const segments = options.segments ?? detectSegmentsFromPolyline(polyline);
  const startLine = options.startLine ?? buildStartLineAtFraction(polyline, 0, trackWidthM);

  const lapSections = options.lapSections?.length
    ? internalLapSections(options.lapSections, corners)
    : rotateSectionsToStart(buildLapSections(polyline, corners, cornerArcHalfM));
  const lapFeatures = buildLapSectionFeatures(
    polyline,
    lapSections,
    trackWidthM,
    options.authoredSurface,
  );
  const sectorFeatures = options.authoredSurface
    ? []
    : buildSectorFeatures(polyline, segments, trackWidthM);
  const { entry, exit } = startLineToEntryExit(startLine);

  const startFinish: DetailedCircuitFeature = {
    id: 'start-finish',
    parentId: null,
    type: 'StartFinish',
    label: 'Start/Finish',
    entryLine: entry,
    exitLine: exit,
    directionalPoint: toContributionCoord({
      lat: (startLine.pointA.lat + startLine.pointB.lat) / 2,
      lon: (startLine.pointA.lon + startLine.pointB.lon) / 2,
    }),
  };

  const authoredBoundaryFeatures: DetailedCircuitFeature[] = options.authoredSurface
    ? [
        {
          id: 'authored-track-surface',
          parentId: null,
          type: 'TrackSurface',
          label: 'Track surface',
          polygonRings: options.authoredSurface.outlineRings,
        },
        {
          id: 'authored-outside-boundary',
          parentId: null,
          type: 'TrackOutsideBoundary',
          label: 'Outside edge',
          entryLine: options.authoredSurface.outsideLine.map(toContributionCoord),
        },
        {
          id: 'authored-inside-boundary',
          parentId: null,
          type: 'TrackInsideBoundary',
          label: 'Inside edge',
          entryLine: options.authoredSurface.insideLine.map(toContributionCoord),
        },
      ]
    : [];

  return withDerivedGeometricApexes({
    version: 1,
    outlineRings,
    centerLine,
    features: [
      ...authoredBoundaryFeatures,
      startFinish,
      ...lapFeatures,
      ...sectorFeatures,
      ...(options.customFeatures ?? []),
    ],
  });
}

export function buildContributionPayloadWithDetailedGeometry(
  trackId: string,
  geometry: RecalibratedTrackGeometry,
  corners: CornerApexSuggestion[],
  confidenceScore: number,
  options: BuildDetailedGeometryOptions = {},
): TrackContributionPayload {
  const base = buildContributionDraftPayload(trackId, geometry, corners, confidenceScore);
  const detailedGeometry = buildDetailedGeometryFromCenterline(geometry.polyline, {
    ...options,
    corners,
    segments: geometry.segmentSuggestions,
    startLine: geometry.startLineSuggestion,
  });

  return {
    ...base,
    detailedGeometry,
    detailedGeometryVersion: 1,
    detailedGeometryFeatureCount: detailedGeometry.features.length,
  };
}

/** Buffer a centerline (open or closed) into a corridor outline for live map preview. */
export function buildCorridorOutlineFromCenterline(
  sourcePolyline: GpsCoord[],
  trackWidthM: number,
  closedCircuit = false,
): GpsCoord[] {
  if (sourcePolyline.length < 2 || trackWidthM <= 0) {
    return [];
  }

  const rings = buildCorridorOutlineRings(sourcePolyline, trackWidthM, closedCircuit);
  return outlineRingsToMapCoords(rings);
}

export function buildCorridorOutlineRings(
  sourcePolyline: GpsCoord[],
  trackWidthM: number,
  closedCircuit = false,
): TrackContributionGpsCoord[][] {
  if (sourcePolyline.length < 2 || trackWidthM <= 0) {
    return [];
  }

  return bufferPolylineToRings(
    centerlineCoordsForBuffer(sourcePolyline, closedCircuit),
    trackWidthM / 2,
  );
}

export function lapSectionFeatures(geometry: DetailedCircuitGeometry) {
  return geometry.features.filter((feature) => {
    const type = feature.type.toLowerCase();
    return type === 'straight' || type === 'corner';
  });
}

export function outlineRingsToMapCoords(outlineRings: TrackContributionGpsCoord[][]): GpsCoord[] {
  const ring = outlineRings[0] ?? [];
  if (ring.length < 2) {
    return [];
  }

  const closed = [...ring.map((point) => ({ lat: point.lat, lon: point.lon }))];
  if (haversineM(closed[0]!, closed.at(-1)!) > 1) {
    closed.push(closed[0]!);
  }
  return closed;
}
